import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of, BehaviorSubject, Subject } from 'rxjs';
import { map, mergeMap, refCount, switchMap, tap, publishLast, publishReplay } from 'rxjs/operators';
import { deserialize, dataMember, typedArray, deserializeArray } from 'santee-dcts';
import { SampleTrackerData } from './group-info/study-group-info';
import { Settings } from '../core';
import { CreateGroupRequest } from './create-study-group/create-group-request';
import { StudyGroup } from './group-info/study-group';
import { PortfolioFilterRequestData, ProtocolFilterData, SampleCountData, SampleTrackingSampleCountRequest } from './dashboard/overview-dashboard/dashboard-info';
import { EditGroupRequest } from './edit-study-group/edit-study-group-request';
import { GroupTrackingGridData, SamplesGridRequestInfo, DynamicListRequestInfo, DataRevisionInfo } from './filter-samples/filter-samples-info';
import { StudyGroupItems } from './reports/interfaces/study-group-items';
import { EmailPreference, EmailPreferenceCategory, EmailPreferenceReadModel } from '../user/email-preferences/email';
import { ActivityLogger } from '../activities/activity-logger';
import { User } from '../user';
import { SubjectSearchReadModel, SubjectSearchData, SubjectVisitSampleListReadModel } from './tracking-overview/interfaces/subject-search-data';
import * as moment from 'moment';
import { UserConfigInfo, SaveUserConfigRequest, DynamicListViewColumnData } from './filter-samples/interface/dynamic-list-info';

export class SampleTrackerReadModel {
    @dataMember()
    @typedArray(SampleTrackerData)
    public sampleTrackerData: SampleTrackerData[];
}

export class StudyGroupViewReadModel {
    @dataMember()
    @typedArray(StudyGroup)
    public studyGroups: StudyGroup[];
}

export class PortfolioFilterReadModel {
    @dataMember()
    @typedArray(ProtocolFilterData)
    public portfolioFilterData: ProtocolFilterData[];
}

export class SampleCountReadModel {
    @dataMember()
    @typedArray(SampleCountData)
    public sampleCountData: SampleCountData[];
}

export class GroupTrackingGridReadModel {
    @dataMember()
    public groupTrackingGridData: GroupTrackingGridData[];

    @dataMember()
    public dynamicListViewGridData: GroupTrackingGridData[];
}

@Injectable()
export class SampleTrackingStorage {

    public sampleSearchData: Observable<any>;
    public gridTrackingData = new BehaviorSubject<GroupTrackingGridData[]>([]);
    public gridData: Observable<GroupTrackingGridData[]> = this.gridTrackingData.asObservable();
    public gridColData = new BehaviorSubject<DynamicListViewColumnData | null>(null);
    public gridColumnData: Observable<DynamicListViewColumnData | null> = this.gridColData.asObservable();


    private readonly createGroupUrl = 'sponsor/sampletracker/creategroup';
    private readonly refresher$ = new Subject<void>();
    private readonly updateGroupUrl = 'sponsor/sampletracker/update';

    private readonly studyGroups: Observable<StudyGroup[]>;

    private loadAll$ = new BehaviorSubject<boolean>(true);

    constructor(
        private settings: Settings,
        private http: HttpClient,
        private activityLogger: ActivityLogger) {

        this.studyGroups = this.loadAll$
            .pipe(
                mergeMap(() => of(this.loadStudyGroups())),
                switchMap(x => x),
                publishReplay(1),
                refCount()
            );
    }

    public get all() {
        return this.studyGroups;
    }

    public forceLoadAll() {
        this.loadAll$.next(true);
    }

    public loadStudies(): Observable<SampleTrackerData[]> {
        const getLanguage = () => (navigator.languages && navigator.languages.length && navigator.languages[0]) || navigator.language || 'en';
        return this.http.get(`${this.settings.apiUrl}/sponsor/sampletracker?locale=${getLanguage()}`)
            .pipe(
                map((response) => {
                    let studyData = deserialize(response, SampleTrackerReadModel).sampleTrackerData;
                    studyData = studyData.sort((a, b) => a.protocolCode.localeCompare(b.protocolCode));
                    return studyData;
                }),
                publishLast(),
                refCount(),
            );
    }

    public loadStudyGroups(): Observable<StudyGroup[]> {

        return this.http.get(`${this.settings.apiUrl}/sponsor/sampletracker/studygroups`)
            .pipe(
                map(rawData => {
                    let studyGroups = deserialize(rawData, StudyGroupViewReadModel).studyGroups;
                    studyGroups = studyGroups.sort((a, b) => a.studyGroupName.localeCompare(b.studyGroupName));
                    return studyGroups;
                }),
            );
    }

    public async createStudyGroup(createStudyGroupRequest: CreateGroupRequest) {

        return this.http.post(`${this.settings.apiUrl}/${this.createGroupUrl}`, createStudyGroupRequest);
    }

    public deleteStudyGroup(studyGroupId: number) {

        return this.http.delete(`${this.settings.apiUrl}/sponsor/sampletracker/delete/${studyGroupId}`);
    }

    public editStudyGroup(editRequest: EditGroupRequest) {

        return this.http.post(`${this.settings.apiUrl}/${this.updateGroupUrl}`, editRequest);
    }

    public getPortfolioFilterData(portfolioFilterRequestData: PortfolioFilterRequestData[]): Observable<ProtocolFilterData[]> {
        return this.http.post(`${this.settings.apiUrl}/sponsor/sampletracker/groupportfolio/groupfilter`, portfolioFilterRequestData)
            .pipe(
                map((response) => deserialize(response, PortfolioFilterReadModel).portfolioFilterData),
                publishLast(),
                refCount(),
            );
    }

    public getSampleCountData(countFilterRequest: SampleTrackingSampleCountRequest): Observable<SampleCountData> {
        if (!countFilterRequest || countFilterRequest.filterId.length === 0) {
            return this.loadEmptyCountData();
        }
        return this.http.post(`${this.settings.apiUrl}/sponsor/sampletracker/groupportfolio/samplecount`, countFilterRequest).pipe(
            map((rawData: SampleCountReadModel) => {
                return rawData.sampleCountData[0];
            }),
        );
    }

    public getSamplesGridData(requestInfo: SamplesGridRequestInfo): Observable<{ data: GroupTrackingGridData[], isCached: boolean }> {
        if (!requestInfo.protocolId || requestInfo.protocolId.length === 0) { return of({ data: [], isCached: true }); }
        let cachedData: GroupTrackingGridData[] = [];
        this.gridData.subscribe(value => {
            cachedData = value;
        });
        if (cachedData && cachedData.length > 0) {
            this.clearGridData();
            return of({ data: cachedData, isCached: true });
        }
        return this.http.post(`${this.settings.apiUrl}/sponsor/sampletracker/grouptracking/griddata`, requestInfo).pipe(
            map((rawData: GroupTrackingGridReadModel) => {
                return { data: rawData.groupTrackingGridData, isCached: false };
            }),
        );
    }

    public getDynamicListView(requestInfo: DynamicListRequestInfo): Observable<{ data: GroupTrackingGridData[], isCached: boolean }> {
        let cachedData: GroupTrackingGridData[] = [];
        this.gridData.subscribe(value => {
            cachedData = value;
        });
        if (cachedData && cachedData.length > 0) {
            this.clearGridData();
            return of({ data: cachedData, isCached: true });
        }
        return this.http.post(`${this.settings.apiUrl}/sponsor/sampletracker/grouptracking/griddatadynamiccolumns`, requestInfo).pipe(
            map((rawData: GroupTrackingGridReadModel) => {
                if (!rawData || !rawData.dynamicListViewGridData || !rawData.dynamicListViewGridData.length) {
                    return { data: [], isCached: false };
                }
                return { data: rawData.dynamicListViewGridData, isCached: false};
            }),
        );
    }

    public getSampleSearch(accessionNo: string, containerNo?: number): Observable<any> {
        let params = this.constructParams('containerNumber', containerNo ? containerNo.toString() : '');
        return this.http.get(`${this.settings.apiUrl}/sponsor/sampletracker/search/${accessionNo}`, { params });
    }

    public getVisitData(accessionNo: string, containerNo?: string): Observable<any> {
        return this.http.get(`${this.settings.apiUrl}/sponsor/sampletracker/search/visit/${accessionNo}`);
    }

    public getSampleHeaderDetails(accessionNo: string, containerNo?: string): Observable<any> {
        let params = this.constructParams('containerNumber', containerNo);
        return this.http.get(`${this.settings.apiUrl}/sponsor/sampletracker/search/samplesearch/${accessionNo}`, { params });
    }

    public getSampleDetails(accessionNo: string, status: string, containerNo?: string): Observable<any> {
        let params = this.constructParams('containerNumber', containerNo);
        return this.http.get(`${this.settings.apiUrl}/sponsor/sampletracker/search/currentsamplestatus/${accessionNo}/${status}`, { params });
    }

    public getReportData(accessionNo: string, barcodeNo?: string, containerNo?: string): Observable<any> {
        let params = this.constructParams('barcode', barcodeNo) as HttpParams;
        if (containerNo) {
            params = params.append('containerNumber', containerNo);
        }
        return this.http.get(`${this.settings.apiUrl}/sponsor/sampletracker/search/report/${accessionNo}`, { params });
    }

    public getSampleActivityData(containerid: number): Observable<any> {
        return this.http.get(`${this.settings.apiUrl}/sponsor/sampletracker/search/sampleactivity/${containerid}`);
    }

    public getOpenEqueryQuery(accessionNo: string): Observable<any> {
        return this.http.get(`${this.settings.apiUrl}/sponsor/sampletracker/search/equery/${accessionNo}`);
    }

    public formatedStudyGroups(): Observable<StudyGroupItems[]> {
        return this.studyGroups
            .pipe(
                map(studyGroups => {
                    const groupedItems: StudyGroupItems[] = [];
                    [...new Set(studyGroups.map(sg => sg.studyGroupName))]
                        .forEach((sgn: string) => {
                            const filteredStudyGroups = studyGroups.filter(sg => sg.studyGroupName === sgn);

                            if (filteredStudyGroups && filteredStudyGroups.length) {
                                groupedItems.push({
                                    id: filteredStudyGroups[0].groupPk,
                                    studyGroupName: sgn,
                                    createDbDate: filteredStudyGroups[0].createDbDate,
                                    updateDbDate: filteredStudyGroups[0].updateDbDate,
                                    groupPk: filteredStudyGroups.map(fsg => fsg.groupPk),
                                    protocolCode: filteredStudyGroups.map(fsg => fsg.protocolCode),
                                    protocolFk: filteredStudyGroups.map(fsg => fsg.protocolFk),
                                    sponsorName: filteredStudyGroups.map(fsg => fsg.sponsorName),
                                    protocolCodeAndSponsorName: filteredStudyGroups.map(fsg => {
                                        return ({
                                            protocolCode: fsg.protocolCode,
                                            sponsorName: fsg.sponsorName
                                        });
                                    }),
                                    protocolData: filteredStudyGroups.map(fsg => {
                                        return ({
                                            name: fsg.protocolCode,
                                            id: fsg.protocolFk
                                        });
                                    })
                                });
                            }
                        });
                    return groupedItems;
                }));
    }

    public async saveEmailPreferences(emailPreferences: EmailPreference[], userPk: number): Promise<void> {
        this.activityLogger.logChangeEmailPreferencesActivity(emailPreferences, userPk);

        await this.http.put(`${this.settings.apiUrl}/email-preferences/user/${userPk}`, emailPreferences)
            .toPromise();
    }

    public getDynamicListViewColumnInfo(listViewName: string): Observable<any> {
        listViewName = 'sample-tracking-' + listViewName;
        let cachedData: DynamicListViewColumnData;
        this.gridColumnData.subscribe(value => {
            cachedData = value;
        });
        if (cachedData) {
            return of(cachedData);
        }
        return this.http.get(`${this.settings.apiUrl}/sponsor/sampletracker/grouptracking/columnpickerdata/${listViewName}`).pipe(
            map((result: any) => {
                return result.gridDataColumnPickerData;
            }
        ));
    }

    public loadEmailPreferenceCategories(user: User): Observable<EmailPreferenceCategory[]> {
        return this.http
            .get<Object[]>(`${this.settings.apiUrl}/sample-tracking/email-preferences/categories`)
            .pipe(
                map((response: Object[]) => deserializeArray(response, EmailPreferenceCategory)),
                publishLast(),
                refCount()
            );
    }

    public getUserEmailPreferences(user: User) {
        return this.http
            .get<Object[]>(`${this.settings.apiUrl}/sample-tracking/email-preferences/level/0/user/${user.metadata.userId}`)
            .pipe(map((response: EmailPreferenceReadModel[]) => {
                let result = deserializeArray(response, EmailPreferenceReadModel);
                if (!result || result.length === 0) { return null; }
                let defaultEmail = result.filter(t => t.email.isDefault).map(t => t);
                if (!defaultEmail || defaultEmail.length === 0) { return result[0]; }
                return defaultEmail[0];
            }));

    }

    public getDataRevisionInfo(url: string): Observable<DataRevisionInfo[]> {
        return this.http.get(`${this.settings.apiUrl}/sponsor/sampletracker/grouptracking/${url}`).pipe(
            map((result: any) => {
                return result.revisionData;
            })
        );
    }

    public subjectSearchResults(requestData: any): Observable<SubjectSearchData[]> {
        return this.http.post(`${this.settings.apiUrl}/sponsor/sampletracker/search/subjectidsearch`, requestData).
            pipe(map((searchResults: SubjectSearchReadModel) => {
                if (!searchResults || !searchResults.subjectSearchData || searchResults.subjectSearchData.length === 0) {
                    return [];
                }
                searchResults.subjectSearchData.forEach(data => {
                    data.visits = '';
                    data.samples = '';
                    data.displayAge = data.birthDate && data.birthDate.length > 0 ? moment(data.birthDate).format('DD-MMM-YYYY') : data.ageProjectDisplayValue;
                });
                return searchResults.subjectSearchData;
            }));
    }

    public visitBySubjectID(requestData: any): Observable<SubjectSearchData[]> {
        return this.http.post(`${this.settings.apiUrl}/sponsor/sampletracker/search/visitbypatientnumber`, requestData).
            pipe(map((searchResults: SubjectVisitSampleListReadModel) => {
                if (!searchResults || !searchResults.visitListData || searchResults.visitListData.length === 0) {
                    return [];
                }
                return searchResults.visitListData;
            }));
    }

    public sampleBySubjectID(requestData: any): Observable<SubjectSearchData[]> {
        return this.http.post(`${this.settings.apiUrl}/sponsor/sampletracker/search/samplebysubjectid`, requestData).
            pipe(map((searchResults: SubjectVisitSampleListReadModel) => {
                if (!searchResults || !searchResults.sampleListData || searchResults.sampleListData.length === 0) {
                    return []; 
                }
                return searchResults.sampleListData;
            }));
    }

    public async saveUserConfigRequest(userConfig: UserConfigInfo[], definitionId: number): Promise<void> {
        let requestData: SaveUserConfigRequest = {
            appDataDefinitionId: definitionId,
            configJson: JSON.stringify(userConfig)
        };
        await this.http.put(`${this.settings.apiUrl}/sponsor/sampletracker/grouptracking/savedynamiclistconfig`, requestData).toPromise();
    }

    public async resetColumnSettings(appDataDefinitionId: number): Promise<void> {
        await this.http.delete(`${this.settings.apiUrl}/sponsor/sampletracker/grouptracking/resetdynamiclistconfig/${appDataDefinitionId}`).toPromise();
    }

    public clearGridData() {
        this.gridData = of([]);
        this.gridColumnData = of(null);
    }

    private loadEmptyCountData(): Observable<SampleCountData> {
        let count = new SampleCountData();
        count.samplesInInventory = 0;
        count.samplesNotReceived = 0;
        count.samplesReceived = 0;
        count.samplesShipped = 0;
        count.samplesDiscarded = 0;
        count.samplesOnHold = 0;
        count.samplesCollected = 0;
        count.samplesCancelled = 0;
        count.samplesRemoved = 0;
        count.samplesShippedFromSiteToThirdParty = 0;
        count.samplesShippedFromSiteToLabcorp = 0;
        return new Observable(ob => ob.next(count));
    }

    private constructParams(param: string, value: string) {
        let params = {};
        if (value) {
            params = new HttpParams().set(param, value);
        }
        return params;
    }

}